import { useTheme, useMediaQuery } from '@mui/material';
import { useEffect, useState, useContext } from 'react';
import { AuthContext, useApi, API } from '@plumeuk/shapeshift-identity';
import 'x2js';

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

/* eslint-disable no-console */
//Hook to be used to log dependency changes - debugging tool
function useDepLog(tag, dep, level) {
    useEffect(() => {
        const descriptive = `---> Dependency change \t${tag}\t`;
        const value = typeof dep == "function" ? "function" : dep;
        if (level && level === "warn")
            console.warn(descriptive, value);
        if (level && level === "error")
            console.error(descriptive, value);
        else
            console.log(descriptive, value);
    }, [tag, dep, level]);
}

// Our hook
function useDebounce(value, delay) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        // Set debouncedValue to value (passed in) after the specified delay
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        // Return a cleanup function that will be called every time ...
        // ... useEffect is re-called. useEffect will only be re-called ...
        // ... if value changes (see the inputs array below). 
        // This is how we prevent debouncedValue from changing if value is ...
        // ... changed within the delay period. Timeout gets cleared and restarted.
        // To put it in context, if the user is typing within our app's ...
        // ... search box, we don't want the debouncedValue to update until ...
        // ... they've stopped typing for more than 500ms.
        return () => {
            clearTimeout(handler);
        };
    }, 
    // Only re-call effect if value changes
    // You could also add the "delay" var to inputs array if you ...
    // ... need to be able to change that dynamically.
    [value, delay]);
    return debouncedValue;
}

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

//Get module in tree
//Default latest active, if request passed returns the matching module or undefined
const getModule = (course, request, includeUnavailable) => {
    const n = (modules) => {
        let module;
        modules.forEach(e => {
            if (module)
                return;
            if (e.type !== "section" && ((request && request.slug == e.slug && request.type == e.type)
                || (!request && !e.complete && (includeUnavailable || !e.availableFrom || e.availableFrom < new Date())))) {
                module = e;
                return;
            }
            else if (e.modules) {
                module = n(e.modules);
                if (module)
                    return;
            }
        });
        return module;
    };
    if (!course || !course.modules)
        return;
    return n(course?.modules);
};

const useCourseCurriculum = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.course(courseSlug), data);
    const [course, setCourse] = useState(data);
    useEffect(() => setCourse(apiResult.data), [apiResult.data]);
    useEffect(() => setCourse(data), [data]);
    const setModuleComplete = (moduleRequest, status) => {
        //handle internal update
        if (!data && course) {
            setCourse(prev => {
                const module = getModule(course, moduleRequest);
                if (!prev || !module || module.complete == status)
                    return prev;
                if (module.progression !== false) {
                    prev.modulesCompleteCount += (status ? 1 : -1);
                    prev.percentCompleted = Math.round((prev.modulesCompleteCount / prev.modulesCount) * 100);
                }
                module.complete = status;
                return { ...prev };
            });
        }
    };
    const setModule = (moduleRequest, prop, value) => {
        if (!data && course) {
            setCourse(prev => {
                const module = getModule(course, moduleRequest);
                if (prop === "complete")
                    throw new Error("Use setModuleComplete instead");
                if (!prev || !module || module[prop] == value)
                    return prev;
                module[prop] = value;
                return { ...prev };
            });
        }
    };
    const getCourse = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.course(slug) }
            : API.course(slugOverride ?? slug));
    };
    return { course, apiResult, getCourse, setCourse, setModuleComplete, setModule };
};

const useCourseCurriculumPublic = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.courseCurriculum(courseSlug), data);
    const [courseCurriculumPublic, setCourseCurriculumPublic] = useState(data);
    useEffect(() => setCourseCurriculumPublic(apiResult.data), [apiResult.data]);
    useEffect(() => setCourseCurriculumPublic(data), [data]);
    const getCourseCurriculumPublic = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseCurriculum(slug) }
            : API.courseCurriculum(slugOverride ?? slug));
    };
    return { courseCurriculumPublic, apiResult, getCourseCurriculumPublic, setCourseCurriculumPublic };
};

const useCourseCertificate = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.courseCertificate(courseSlug), data);
    const [certificate, setCertificate] = useState();
    useEffect(() => setCertificate(apiResult.data), [apiResult.data]);
    useEffect(() => setCertificate(data), [data]);
    const getCertificate = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseCertificate(slug) }
            : API.courseCertificate(slug));
    };
    useEffect(() => data && setCertificate(data), [data]);
    return { certificate, apiResult, getCertificate, setCertificate };
};

const useCourseAvailableModuleProgression = (slug, data) => {
    const [apiResult, getData, setCourseAvailableModuleProgression] = useApi(data || !slug ? null : API.courseAvailableModuleProgression(slug), data);
    const getCourseAvailableModuleProgression = (slugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseAvailableModuleProgression(_slug) }
            : API.courseAvailableModuleProgression(_slug));
    };
    useEffect(() => {
        if (data)
            return;
        getCourseAvailableModuleProgression(slug);
    }, [slug, data]);
    useEffect(() => data && setCourseAvailableModuleProgression(data), [data]);
    return { result: apiResult.data, apiResult, getCourseAvailableModuleProgression, setCourseAvailableModuleProgression };
};

const useCourses = (showProgress, data) => {
    const [apiResult, getData, setCourses] = useApi(data ? null : API.courses(showProgress ?? true), data);
    const getCourses = (showProgressOveride, configOverride) => {
        const _showProgress = showProgressOveride ?? showProgress;
        getData(configOverride
            ? { ...configOverride, url: API.courses(_showProgress ?? true) }
            : API.courses(_showProgress ?? true));
    };
    useEffect(() => data && setCourses(data), [data]);
    return { courses: apiResult.data, apiResult, getCourses, setCourses };
};

const useLesson = (courseSlug, lessonSlug, data, populate) => {
    const [apiResult, getData, setLesson] = useApi(data || !courseSlug || !lessonSlug ? null : { url: API.lesson(courseSlug, lessonSlug), data: { populate } }, data);
    const getLesson = (courseSlugOverride, lessonSlugOverride, configOverride) => {
        const _lessonSlug = lessonSlugOverride ?? lessonSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_lessonSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.lesson(_courseSlug, _lessonSlug) }
            : API.lesson(_courseSlug, _lessonSlug));
    };
    useEffect(() => data && setLesson(data), [data]);
    return { lesson: apiResult.data, apiResult, getLesson, setLesson };
};

const useQuiz = (courseSlug, quizSlug, data) => {
    const [apiResult, getData, setQuiz] = useApi(data || !courseSlug || !quizSlug ? null : API.quiz(courseSlug, quizSlug), data);
    const getQuiz = (courseSlugOverride, quizSlugOverride, configOverride) => {
        const _quizSlug = quizSlugOverride ?? quizSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_quizSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.quiz(_courseSlug, _quizSlug) }
            : API.quiz(_courseSlug, _quizSlug));
    };
    useEffect(() => data && setQuiz(data), [data]);
    return { quiz: apiResult.data, apiResult, getQuiz, setQuiz };
};

const useQuizQuestions = (courseSlug, quizSlug, data) => {
    const [apiResult, getData, setQuizQuestions] = useApi(data || !courseSlug || !quizSlug ? null : API.quizQuestions(courseSlug, quizSlug), data);
    const getQuizQuestions = (courseSlugOverride, quizSlugOverride, configOverride) => {
        const _quizSlug = quizSlugOverride ?? quizSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_quizSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.quizQuestions(_courseSlug, _quizSlug) }
            : API.quizQuestions(_courseSlug, _quizSlug));
    };
    useEffect(() => data && setQuizQuestions(data), [data]);
    return { quizQuestions: apiResult.data, apiResult, getQuizQuestions, setQuizQuestions };
};

const useSurvey = (courseSlug, surveySlug, data) => {
    const [apiResult, getData, setSurvey] = useApi(data || !courseSlug || !surveySlug ? null : API.survey(courseSlug, surveySlug), data);
    const getSurvey = (courseSlugOverride, surveySlugOverride, configOverride) => {
        const _surveySlug = surveySlugOverride ?? surveySlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_surveySlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.survey(_courseSlug, _surveySlug) }
            : API.survey(_courseSlug, _surveySlug));
    };
    useEffect(() => data && setSurvey(data), [data]);
    return { survey: apiResult.data, apiResult, getSurvey, setSurvey };
};

const useSurveyQuestions = (courseSlug, surveySlug, data) => {
    const [apiResult, getData, setSurveyQuestions] = useApi(data || !courseSlug || !surveySlug ? null : API.surveyQuestions(courseSlug, surveySlug), data);
    const getSurveyQuestions = (courseSlugOverride, surveySlugOverride, configOverride) => {
        const _surveySlug = surveySlugOverride ?? surveySlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_surveySlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.surveyQuestions(_courseSlug, _surveySlug) }
            : API.surveyQuestions(_courseSlug, _surveySlug));
    };
    useEffect(() => {
        if (data)
            setSurveyQuestions(data);
        else
            getSurveyQuestions(courseSlug, surveySlug);
    }, [data, surveySlug, courseSlug]);
    return { surveyQuestions: apiResult.data, apiResult, getSurveyQuestions, setSurveyQuestions };
};

const useSetModuleCompletion = () => {
    const [apiResult, getData] = useApi(null);
    const setComplete = (courseSlug, moduleSlug, type, status, config) => getData({
        ...(config ?? {}),
        url: API.moduleComplete(courseSlug, moduleSlug, type, status),
        method: "POST",
        cache: false
    });
    return { response: apiResult.data, apiResult, setComplete };
};

const useMarkQuizAnswers = () => {
    const [apiResult, getData] = useApi(null);
    const markAnswers = (courseSlug, quizSlug, answers, config) => getData({
        ...(config ?? {}),
        data: answers,
        url: API.quizMark(courseSlug, quizSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, markAnswers };
};

const useMarkedQuizResult = (courseSlug, quizSlug, resultId, data) => {
    const [apiResult, getData, setMarkedQuizResult] = useApi(data || !courseSlug || !quizSlug ? null : API.quizMarkResult(courseSlug, quizSlug, resultId), data);
    const getMarkedQuizResult = (courseSlugOverride, quizSlugOverride, idOverride, configOverride) => {
        const _courseSlug = courseSlugOverride ?? courseSlug;
        const _quizSlug = quizSlugOverride ?? quizSlug;
        const _resultId = idOverride ?? resultId;
        if (!_quizSlug || !_resultId || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.quizMarkResult(_courseSlug, _quizSlug, _resultId) }
            : API.quizMarkResult(_courseSlug, _quizSlug, _resultId));
    };
    useEffect(() => data && setMarkedQuizResult(data), [data]);
    return { markedQuizResult: apiResult.data, apiResult, getMarkedQuizResult, setMarkedQuizResult };
};

const useLibrary = (search, data) => {
    const [apiResult, getData, setLibrary] = useApi(data ? null : { url: API.library, data: search }, data);
    const getLibrary = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.library, data }
            : { url: API.library, data });
    };
    useEffect(() => data && setLibrary(data), [data]);
    return { items: apiResult.data, apiResult, getLibrary, setLibrary };
};

const useLibraryTags = (options, data) => {
    const [apiResult, getData, setTags] = useApi(data ? null : { url: API.libraryTags, data: options }, data);
    const getTags = (configOverride) => {
        getData(configOverride
            ? { ...configOverride, url: API.courseTags, data: options }
            : { url: API.libraryTags, data: options });
    };
    useEffect(() => data && setTags(data), [data]);
    return { tags: apiResult.data, apiResult, getTags, setTags };
};

const useLibraryCourses = (search, data) => {
    const [apiResult, getData, setCourses] = useApi(data ? null : { url: API.libraryCourses, data: search }, data);
    const getCourses = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.libraryCourses, data }
            : { url: API.libraryCourses, data });
    };
    useEffect(() => data && setCourses(data), [data]);
    return { courses: apiResult.data, apiResult, getCourses, setCourses };
};

const useModulesUpcoming = (courseSlug, data) => {
    const [apiResult, getData, setUpcoming] = useApi(data || !courseSlug ? null : API.upcoming(courseSlug), data);
    const getUpcoming = (courseSlugOverride, configOverride) => {
        const slug = courseSlugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.upcoming(slug) }
            : API.upcoming(slug));
    };
    useEffect(() => data && setUpcoming(data), [data]);
    return { upcoming: apiResult.data, apiResult, getUpcoming, setUpcoming };
};

const useCommentPoster = () => {
    const [apiResult, getData] = useApi(null);
    const postComment = (comment, moduleType, courseSlug, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: comment,
        url: API.comments(courseSlug, moduleType, moduleSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, postComment };
};

const useLibraryCourse = (slug, groupSlug, data) => {
    const [apiResult, getData, setCourse] = useApi(data || !slug ? null : API.libraryCourse(slug, groupSlug), data);
    const getCourse = (slugOverride, groupSlugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _groupSlugOverride = groupSlugOverride ?? groupSlug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.libraryCourse(_slug, _groupSlugOverride) }
            : API.libraryCourse(_slug, _groupSlugOverride));
    };
    useEffect(() => data && setCourse(data), [data]);
    return { course: apiResult.data, apiResult, getCourse, setCourse };
};

const useLibraryGroup = (slug, showProgress, data) => {
    const [apiResult, getData, setGroup] = useApi(data || !slug ? null : API.libraryGroup(slug), data);
    const getGroup = (slugOverride, showProgressOveride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _showProgress = showProgressOveride ?? showProgress;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.libraryGroup(_slug, _showProgress ?? true) }
            : API.libraryGroup(_slug, _showProgress ?? true));
    };
    useEffect(() => data && setGroup(data), [data]);
    return { group: apiResult.data, apiResult, getGroup, setGroup };
};

const useNotes = (courseSlug, moduleType, moduleSlug, data) => {
    const [apiResult, getData, setNotes] = useApi((data || !courseSlug || !moduleSlug || !moduleType) ? null : API.notes(courseSlug, moduleType, moduleSlug), data);
    const getNotes = (courseSlugOverride, typeOverride, moduleSlugOverride, configOverride) => {
        const _type = typeOverride ?? moduleType;
        const _moduleSlug = moduleSlugOverride ?? moduleSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_courseSlug || !_moduleSlug || !_type)
            return;
        const url = API.notes(_courseSlug, _type, _moduleSlug);
        getData(configOverride ? { ...configOverride, url } : url);
    };
    useEffect(() => data && setNotes(data), [data]);
    return { notes: apiResult.data, apiResult, getNotes, setNotes };
};

const useWebinar = (courseSlug, webinarSlug, data) => {
    const [apiResult, getData, setWebinar] = useApi(data || !courseSlug || !webinarSlug ? null : API.webinar(courseSlug, webinarSlug), data);
    const getWebinar = (courseSlugOverride, webinarSlugOverride, configOverride) => {
        const _webinarSlug = webinarSlugOverride ?? webinarSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_webinarSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.webinar(_courseSlug, _webinarSlug) }
            : API.webinar(_courseSlug, _webinarSlug));
    };
    useEffect(() => data && setWebinar(data), [data]);
    return { webinar: apiResult.data, apiResult, getWebinar, setWebinar };
};

const useNotePoster = () => {
    const [apiResult, getData] = useApi(null);
    const postNote = (note, courseSlug, moduleType, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: note,
        url: API.notes(courseSlug, moduleType, moduleSlug),
        method: "POST"
    });
    const editNote = (id, content, config) => getData({
        ...(config ?? {}),
        data: { content },
        url: API.noteModify(id),
        method: "PATCH"
    });
    const deleteNote = (id, _content, config) => getData({
        ...(config ?? {}),
        url: API.noteModify(id),
        method: "DELETE"
    });
    return { response: apiResult.data, apiResult, postNote, editNote, deleteNote };
};

const useSetCourseEnrollment = () => {
    const [apiResult, getData] = useApi(null);
    const setEnroll = (courseSlug, config) => getData({
        ...(config ?? {}),
        url: API.courseEnroll(courseSlug),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, setEnroll };
};

const useSetCourseUnEnrollment = () => {
    const [apiResult, getData] = useApi(null);
    const setUnEnroll = (courseSlug, config) => getData({
        ...(config ?? {}),
        url: API.courseUnEnroll(courseSlug),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, setUnEnroll };
};

const useSubmitSurveyResponse = () => {
    const [apiResult, getData] = useApi(null);
    const submitResponse = (courseSlug, quizSlug, answers, config) => getData({
        ...(config ?? {}),
        data: answers,
        url: API.surveySubmitResponse(courseSlug, quizSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, submitResponse };
};

const useSaveLessonVideoTime = () => {
    const [apiResult, getData] = useApi(null);
    const saveVideoTime = (time, courseSlug, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: { time },
        url: API.saveLessonVideoTime(courseSlug, moduleSlug),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, saveVideoTime };
};

const useSetModuleStartTime = () => {
    const [apiResult, getData] = useApi(null);
    const setStartedTime = (courseSlug, moduleSlug, type, config) => getData({
        ...(config ?? {}),
        url: API.saveModuleStartTime(courseSlug, moduleSlug, type),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, setStartedTime };
};

const useSetScormSessionData = () => {
    const [apiResult, getData] = useApi(null);
    const saveSessionData = (courseSlug, moduleSlug, sessionData, config) => getData({
        ...(config ?? {}),
        url: API.scormSaveSessionData(courseSlug, moduleSlug),
        data: { data: sessionData },
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, saveSessionData };
};

const useLibraryCarousels = (search, data) => {
    const [apiResult, getData, setLibraryCarousels] = useApi(data ? null : { url: API.libraryCarousels, data: search }, data);
    const getLibraryCarousels = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.libraryCarousels, data }
            : { url: API.libraryCarousels, data });
    };
    useEffect(() => data && setLibraryCarousels(data), [data]);
    return { carousels: apiResult.data, apiResult, getLibraryCarousels, setLibraryCarousels };
};

const useCourseProgress = (slug, data) => {
    const [apiResult, getData, setProgress] = useApi(data || !slug ? null : API.courseProgress(slug), data);
    const getProgress = (slugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseProgress(_slug) }
            : API.courseProgress(_slug));
    };
    useEffect(() => {
        if (data)
            setProgress(data);
    }, [data]);
    return { progress: apiResult.data, apiResult, getProgress, setProgress };
};

const useInviteUserToGroup = () => {
    const [apiResult, getData] = useApi(null);
    const inviteUser = (groupSlug, userEmail, config) => getData({
        ...(config ?? {}),
        url: API.groupInviteUser(groupSlug),
        data: { userEmail },
        method: "POST"
    });
    return { response: apiResult.data, apiResult, inviteUser };
};

const useOwnedGroup = (slug, courseSlug, data) => {
    const [apiResult, getData, setGroup] = useApi(null, data);
    const getGroup = (slugOverride, courseSlugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_slug)
            return;
        getData({
            ...(configOverride ?? {}),
            url: API.ownedGroup(_slug),
            data: _courseSlug ? { courseSlug: _courseSlug } : undefined
        });
    };
    useEffect(() => {
        if (data)
            return;
        getGroup(slug, courseSlug);
    }, [slug, courseSlug, data]);
    useEffect(() => data && setGroup(data), [data]);
    return { group: apiResult.data, apiResult, getGroup, setGroup };
};

const useOwnedGroups = (data) => {
    const [apiResult, getData, setGroups] = useApi(data ? null : API.ownedGroups, data);
    const getGroups = (configOverride) => getData(configOverride
        ? { ...configOverride, url: API.ownedGroups }
        : API.ownedGroups);
    useEffect(() => data && setGroups(data), [data]);
    return { groups: apiResult.data, apiResult, getGroups, setGroups };
};

const useMemberedGroup = (slug, courseSlug, data) => {
    const [apiResult, getData, setGroup] = useApi(null, data);
    const getGroup = (slugOverride, courseSlugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_slug)
            return;
        getData({
            ...(configOverride ?? {}),
            url: API.memberedGroup(_slug),
            data: _courseSlug ? { courseSlug: _courseSlug } : undefined
        });
    };
    useEffect(() => {
        if (data)
            return;
        getGroup(slug, courseSlug);
    }, [slug, courseSlug, data]);
    useEffect(() => data && setGroup(data), [data]);
    return { result: apiResult.data, apiResult, getGroup, setGroup };
};

const useMemberedGroups = (data) => {
    const [apiResult, getData, setGroups] = useApi(data ? null : API.memberedGroups, data);
    const getGroups = (configOverride) => getData(configOverride
        ? { ...configOverride, url: API.memberedGroups }
        : API.memberedGroups);
    useEffect(() => data && setGroups(data), [data]);
    return { groups: apiResult.data, apiResult, getGroups, setGroups };
};

const useRefreshInviteUserToGroup = () => {
    const [apiResult, getData] = useApi(null);
    const refreshUserInvite = (groupSlug, userEmail, config) => getData({
        ...(config ?? {}),
        url: API.groupRefreshInviteUser(groupSlug),
        data: { userEmail },
        method: "POST"
    });
    return { response: apiResult.data, apiResult, refreshUserInvite };
};

const useCancelInviteUserToGroup = () => {
    const [apiResult, getData] = useApi(null);
    const cancelUserInvite = (groupSlug, userEmail, config) => getData({
        ...(config ?? {}),
        url: API.groupCancelInviteUser(groupSlug),
        data: { userEmail },
        method: "POST"
    });
    return { response: apiResult.data, apiResult, cancelUserInvite };
};

const useJoinGroup = () => {
    const [apiResult, getData] = useApi(null);
    const joinGroup = (token, config) => getData({
        ...(config ?? {}),
        url: API.groupJoin(),
        data: { token },
        method: "POST"
    });
    return { response: apiResult.data, apiResult, joinGroup };
};

const useBulkInviteToGroupLink = () => {
    const [apiResult, getInviteLink] = useApi();
    const generateInvite = (groupSlug, config) => getInviteLink({
        ...(config ?? {}),
        url: API.groupBulkInviteLink(groupSlug),
        method: "GET"
    });
    return { invite: apiResult.data, apiResult, generateInvite };
};

const useSetGroupUnEnrollment = () => {
    const [apiResult, getData] = useApi(null);
    const setUnEnroll = (groupSlug, config) => getData({
        ...(config ?? {}),
        url: API.groupUnEnroll(groupSlug),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, setUnEnroll };
};

export { useBulkInviteToGroupLink, useCancelInviteUserToGroup, useCommentPoster, useCourseAvailableModuleProgression, useCourseCertificate, useCourseCurriculum, useCourseCurriculumPublic, useCourseProgress, useCourses, useDebounce, useDepLog, useFileBaseUrl, useInviteUserToGroup, useJoinGroup, useLesson, useLibrary, useLibraryCarousels, useLibraryCourse, useLibraryCourses, useLibraryGroup, useLibraryTags, useMarkQuizAnswers, useMarkedQuizResult, useMemberedGroup, useMemberedGroups, useModulesUpcoming, useMq, useNotePoster, useNotes, useOwnedGroup, useOwnedGroups, useQuiz, useQuizQuestions, useRefreshInviteUserToGroup, useSaveLessonVideoTime, useSetCourseEnrollment, useSetCourseUnEnrollment, useSetGroupUnEnrollment, useSetModuleCompletion, useSetModuleStartTime, useSetScormSessionData, useSubmitSurveyResponse, useSurvey, useSurveyQuestions, useWebinar };
